import { ConfirmationDialogService } from 'src/app/services/confirmation-dialog.service';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  forwardRef,
  Input,
  OnInit,
  Renderer2,
  OnDestroy,
  inject
} from '@angular/core';
import { NG_VALUE_ACCESSOR, ControlValueAccessor } from '@angular/forms';
import { Store } from '@ngrx/store';
import { v4 as uuidv4 } from 'uuid';
import { HttpBaseService } from 'src/app/base/http-base.service';
import { AppGlobalService } from 'src/app/services/app.global.service';
import { UtilService } from 'src/app/services/util.service';
import { AutoUnsubscribe } from 'ngx-auto-unsubscribe';
import { Subscription } from 'rxjs/internal/Subscription';
import { cloneDeep } from 'lodash-es';
import { TranslateService } from '@ngx-translate/core';

import { queryMapData } from 'src/app/STORE/selectors/screens.selector';
import { metaInfo } from 'src/app/STORE/selectors/applifecycle.selector';
import { GlobalConstants } from 'src/app/base/global.constants';
import { ApiConstants } from 'src/app/base/api-constants';

@AutoUnsubscribe({ arrayName: 'subscriptions' })
@Component({
  selector: 'vb-query-param-map',
  templateUrl: './query-param-map.component.html',
  styleUrls: ['./query-param-map.component.scss'],
  changeDetection: ChangeDetectionStrategy.Default,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => QueryParamMapComponent),
      multi: true
    }
  ],
  standalone: false //NOSONAR
})
export class QueryParamMapComponent implements OnInit, OnDestroy, ControlValueAccessor {
  private confirmationDialogService = inject(ConfirmationDialogService);
  private renderer = inject(Renderer2);
  private elementRef = inject(ElementRef);
  private store = inject(Store);
  private baseService = inject(HttpBaseService);
  private appGlobalService = inject(AppGlobalService);
  private util = inject(UtilService);
  private translate = inject(TranslateService);
  private modelValue: any;
  private onChange: (m: any) => void;
  private onTouched: (m: any) => void;
  @Input() element: any;
  @Input() formData: any;
  @Input() disableField = false;
  @Input() currentScreen: any;
  @Input() currentNode: any = null;
  @Input() currNodeType: any = null;
  appId = null;
  allFields = [];
  subscriptions: Subscription[] = [];
  queryParamMapping: any[] = [];
  showPopup = false;
  ynOptions = [
    {
      name: 'Yes',
      value: 'yes'
    },
    {
      name: 'No',
      value: 'no'
    }
  ];
  isLoading = false;
  showFilterOptions = false;
  selectedParam = null;
  // viewInPages = false;
  initialLoad = true;
  backupVal: any = null;
  mandatoryFilterSid = '';
  paramMapCalled = true;
  leftFields: string = '';
  rightFields: string = '';
  fieldmismatchwarning: string = '';
  dateOptions = [
    {
      label: 'Yesterday',
      value: 'yesterday'
    },
    {
      label: 'Today',
      value: 'today'
    },
    {
      label: 'Tomorrow',
      value: 'tomorrow'
    }
  ];
  typeOptions: any[] = [
    {
      name: 'Value',
      value: 'value'
    },
    {
      name: 'Page Variable',
      value: 'pageVariable'
    }
  ];
  typeOptionsForDate = [
    ...this.typeOptions,
    { name: '+ Days', value: 'plusDays' },
    { name: '- Days', value: 'minusDays' }
  ];
  typeMap = {
    '': 'Value',
    value: 'Value',
    pageVariable: 'Page Variable',
    plusDays: '+ Days',
    minusDays: '- Days'
  };
  pageVariables = [];
  pageVariablesBck = [];
  noLongerFields: string = '';
  parameterMappingWarning: string = '';
  metaInfo: any;

  get model() {
    return this.modelValue;
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean) {
    this.renderer.setProperty(this.elementRef.nativeElement, 'disabled', isDisabled);
    // this.disabled = isDisabled;
  }

  writeValue(value: any): void {
    this.modelValue = value;
    if (value) {
      this.backupVal = value;
      this.validateFieldMappings(value);
    }
  }

  private validateFieldMappings(value: any): void {
    if (!value || !value.length || !value[0]?.listField?.tableId) {
      return;
    }
    let message = '';
    let deletedIds: any[] = [];
    const fieldInfoSub = this.baseService
      .get(ApiConstants.getFieldInfo, { tableId: value[0].listField.tableId })
      .subscribe({
        next: (data: any) => {
          this.allFields = data;
          value.filter((m) => {
            const lf = this.allFields.find((f: any) => f.sid === m.listField?.sid);
            if (!lf) {
              deletedIds = [...deletedIds, m.listField?.sid];
            }
            this.typeMismatchWarning(m.detailField, lf);
          });
          if (deletedIds.length) {
            this.isLoading = true;
            this.util.handleDeletedIds(deletedIds, false, '', true, 'parammapping', () => {
              this.isLoading = false;
            });
          }
          if (this.leftFields && this.rightFields) {
            this.fieldmismatchwarning = this.translate
              .instant('PARAM_QB_FLD_TYP')
              .replace('{field1}', this.leftFields.slice(0, -1))
              .replace('{field2}', this.rightFields.slice(0, -1));
            this.leftFields = '';
            this.rightFields = '';
            this.confirmationDialogService.confirm({
              message: this.fieldmismatchwarning,
              acceptLabel: this.translate.instant('OK'),
              accept: () => {
                /* empty */
              },
              rejectVisible: false
            });
          }
          this.getNoLongerMessage();
        },
        error: (err) => {
          console.error(err);
        }
      });
    this.addToSubscriptionsList(fieldInfoSub);
  }

  typeMismatchWarning(df, lf) {
    if (df) {
      const dfPageVariable = this.pageVariablesBck.find((p: any) => p.sid === df?.sid);
      if (lf && dfPageVariable) {
        try {
          if (
            !GlobalConstants.paramMappDataTyp[lf?.f_type?.toLowerCase()]?.includes(
              (dfPageVariable['f_type'] as string)?.toLowerCase()
            )
          ) {
            this.leftFields += lf?.name + ',';
            this.rightFields += dfPageVariable['name'] + ',';
          }
        } catch (e) {
          console.error(e);
        }
      }
    }
  }

  set() {
    const queryMap = cloneDeep(this.queryParamMapping)?.map((dt: any, i) => {
      const isNonValueType = dt.type?.value !== 'value' && dt?.detailField;
      const detailFieldObj = isNonValueType ? this.buildDetailFieldObj(dt) : undefined;
      const staticValueObj = isNonValueType ? null : this.buildStaticValueObj(dt);

      if (isNonValueType) {
        this.queryParamMapping[i].staticValue = null;
      } else {
        this.queryParamMapping[i].detailField = null;
      }

      return {
        listField: {
          sid: dt.listField.sid,
          tableId: dt.listField.f_tableId,
          appId: dt.listField.appId,
          type: 'field'
        },
        staticValue: staticValueObj,
        detailField: detailFieldObj
      };
    });
    this.onChange(queryMap?.length ? queryMap : null);
    this.backupVal = cloneDeep(queryMap);
  }

  isNoLongerField(field) {
    if (field) {
      this.noLongerFields += field.name + ',';
    }
  }

  getNoLongerMessage() {
    if (this.noLongerFields) {
      this.noLongerFields = this.noLongerFields.slice(0, -1);
      this.parameterMappingWarning = this.translate
        .instant('PARAM_MAP_NO_LONGER_FLD')
        .replace('{fields}', this.noLongerFields);
      this.noLongerFields = '';
    }
  }

  isFieldNoLonger(data: any): boolean {
    if (data.listField) {
      return this.util.isFieldNoLonger(data, this.allFields, 'query-param-map');
    }
    return false;
  }

  isFieldMismatch(data: any): boolean {
    if (data.type?.value == 'pageVariable' && data.listField && data.detailField) {
      // listField (LHS) is TARGET
      return this.util.isFieldMismatch(data, this.allFields, this.pageVariablesBck, 'query-param-map');
    }
    return false;
  }
  private buildDetailFieldObj(dt: any): any {
    if (dt.type?.value === 'pageVariable') {
      return {
        sid: dt?.detailField?.sid,
        tableId: dt?.detailField?.tableId,
        appId: dt?.detailField?.appId,
        type: dt?.detailField?.type
      };
    }
    // + and - days
    return {
      operator: dt.type?.value,
      value: dt?.detailField,
      type: dt.type?.value
    };
  }

  private buildStaticValueObj(dt: any): any {
    let staticValue = this.processStaticValue(dt);
    const staticValueId = this.backupVal?.find((m) => m?.listField?.sid === dt?.listField?.sid)?.staticValue;
    return staticValue ? { valueSid: staticValueId ? staticValueId?.valueSid : uuidv4(), value: staticValue } : null;
  }

  private processStaticValue(dt: any): any {
    let staticValue = dt.staticValue;
    // send null for multiple value if staticValue is empty
    if (Array.isArray(staticValue) && !staticValue.length) {
      return null;
    }
    if (staticValue && (dt.listField?.f_type === 'Date' || dt.listField?.f_type === 'DateTime')) {
      if (dt.range && (staticValue[0] || staticValue[1])) {
        staticValue[0] = staticValue[0] ? new Date(staticValue[0])?.getTime() : null;
        staticValue[1] = staticValue[1] ? new Date(staticValue[1])?.getTime() : null;
      }
    }
    return staticValue;
  }

  addToSubscriptionsList(subName) {
    this.subscriptions.push(subName);
  }

  operatorChange(event, data) {
    if (event.value && data.staticValue) {
      setTimeout(() => {
        this.set();
      }, 100);
    } else if (data.staticValue) {
      data.staticValue = null;
      setTimeout(() => {
        this.set();
      }, 100);
    }
  }

  onValueChange(event, type?: string) {
    if (type === 'Email' && !this.util.isValidEmail(event.target.value)) {
      this.util.showMessage('error', this.translate.instant('EMAIL_PTERN_MSG'));
    } else {
      setTimeout(() => {
        this.set();
      }, 100);
    }
  }

  formParamMap(queryMap) {
    if (queryMap) {
      const value = queryMap?.tableData;
      const pageVariables = queryMap?.pageVariables?.map((item) => {
        return {
          type: 'pageVariable',
          sid: item.sid,
          name: item.name,
          f_type: GlobalConstants.pageVariableMap[item.type],
          tableId: item.tableSid,
          appId: item.appId
        };
      });
      this.pageVariables = pageVariables || [];
      this.pageVariablesBck = pageVariables || [];
      if (value) {
        this.isLoading = false;
        let mandatoryFilters = [];
        if (value?.t_mandatory_filters) {
          this.mandatoryFilterSid = value?.t_mandatory_filters_sid;
          mandatoryFilters = value?.t_mandatory_filters
            ?.split(',')
            ?.filter((mp) => {
              const fld: any = this.allFields.find((f: any) => f.sid === mp);
              return fld && !this.util.isRestrictedFieldByConstants('paramMappingSource', fld);
            })
            ?.map((m) => {
              const fld: any = this.allFields.find((f: any) => f.sid === m) || {};
              let dynamicOptions,
                range,
                detailField = null,
                type = { name: 'Value', value: 'value' };
              try {

                const screenData = JSON.parse(this.currentScreen?.contentAttachment);
                const filterFields = this.util.findNodeByKeyValue(screenData, 'type', 'quickFilter')?.children;
                
                const filterfld = filterFields?.find((f: any) => f.key === fld.sid);
                if (
                  filterfld &&
                  filterfld.data?.properties?.displayAs &&
                  [
                    'number range',
                    'double range',
                    'currency range',
                    'multi select dropdown',
                    'daterange',
                    'datetimerange'
                  ].includes(filterfld.data?.properties?.displayAs?.toLowerCase())
                ) {
                  range = true;
                }
              } catch (_e) {
                /* left blank */
              }
              if (['Dropdown', 'Radio'].includes(fld.f_type)) {
                if (fld.f_aValues) {
                  try {
                    dynamicOptions = JSON.parse(fld.f_aValues)?.values?.map((op) => {
                      return {
                        label: op && op.name ? op.name : op,
                        value: op && op.sid ? op.sid : op
                      };
                    });
                  } catch (_e) {
                    // Intentionally left blank: ignore JSON parse errors for dynamicOptions
                  }
                } else {
                  dynamicOptions = [];
                }
              }
              let staticValue: any = null;
              // viewInPages = true;
              if (this.initialLoad) {
                const curr = this.backupVal?.find((b) => b.listField?.sid === fld.sid);
                type = curr?.detailField?.type
                  ? { name: this.typeMap[curr?.detailField?.type], value: curr?.detailField?.type }
                  : type;
                if (type?.value === 'pageVariable') {
                  const pageVariableName = this.metaInfo[curr?.detailField?.sid];
                  detailField = pageVariableName
                    ? { ...curr?.detailField, ...{ name: pageVariableName } }
                    : null;
                } else {
                  // plus or minus days
                  detailField = curr?.detailField?.value;
                }
                // range is not supported now in query view
                if (range) {
                  switch (fld.f_type) {
                    case 'Dropdown':
                    case 'Radio':
                      staticValue = curr?.staticValue?.length ? curr?.staticValue : [];
                      break;
                    case 'Date':
                    case 'DateTime':
                      staticValue = [];
                      if (curr?.staticValue && (curr?.staticValue[0] || curr?.staticValue[1])) {
                        staticValue = [
                          curr?.staticValue[0] ? new Date(curr?.staticValue[0]) : null,
                          curr?.staticValue[1] ? new Date(curr?.staticValue[1]) : null
                        ];
                      } else {
                        staticValue = [null, null];
                      }
                      break;
                    default:
                      staticValue = [];
                      if (curr?.staticValue[0] || curr?.staticValue[1]) {
                        staticValue = [
                          curr?.staticValue[0] ? curr.staticValue[0] : null,
                          curr?.staticValue[1] ? curr.staticValue[1] : null
                        ];
                      }
                  }
                } else if (curr?.staticValue && Object.prototype.hasOwnProperty.call(curr.staticValue, 'value')) {
                  staticValue = curr?.staticValue?.value;
                } else {
                  staticValue = curr?.staticValue;
                }
              }
              return {
                listField: fld,
                staticValue: staticValue,
                dynamicOptions: dynamicOptions,
                range: range,
                detailField: detailField,
                type: type
              };
            });
        }
        this.queryParamMapping = [...mandatoryFilters];
        this.initialLoad = false;
      }
    }
  }

  ngOnInit(): void {
    this.appId = this.appGlobalService.get('appId');
    this.metaInfo = this.appGlobalService.get('appmetaInfo');
    const metaInfoSub = this.store.select(metaInfo).subscribe((appmetaInfo: any) => {
      if (appmetaInfo) {
        this.metaInfo = cloneDeep(appmetaInfo);
      }
    });
    this.addToSubscriptionsList(metaInfoSub);
    const queryMapsub = this.store.select(queryMapData).subscribe((queryMap) => {
      if (queryMap) {
        this.allFields = queryMap?.fields;
        if (this.allFields?.length && this.paramMapCalled) {
          this.formParamMap(queryMap);
          this.paramMapCalled = false;
        }
      }
    });
    this.addToSubscriptionsList(queryMapsub);
  }

  ngOnDestroy(): void {
    /* required for AutoUnsubscribe */
  }

  showPageVariables(event, rightField, leftField) {
    // leftField (listField) is TARGET, rightField (pageVariable) is SOURCE
    const filteredFields =
      this.pageVariablesBck?.filter((f: any) =>
        this.util.getSourceTypesForTarget(leftField?.f_type?.toLowerCase(), GlobalConstants.paramMappDataTyp)?.includes(f.f_type.toLowerCase())
      ) || [];
    this.pageVariables = [...filteredFields];
  }

  onPageVariablesSel(event, data) {
    this.onValueChange(event);
  }

  onTypeValueChange(event, index) {
    const mappng = cloneDeep(this.queryParamMapping);
    mappng[index].detailField = null;
    mappng[index].staticValue = null;
    mappng[index].type = event.value;
    this.queryParamMapping = [...mappng];
    this.set();
  }

}



